import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const EmailConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Confirming your email...');
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [resetting, setResetting] = useState(false);
  const hasChecked = useRef(false);
  const passwordResetInitiated = useRef(false);
  const sessionStored = useRef(false);

  // Check URL hash IMMEDIATELY on mount (synchronous check before Supabase processes it)
  useEffect(() => {
    const hash = window.location.hash;
    const type = searchParams.get('type');
    const isRecovery = type === 'recovery' || 
                      hash.includes('access_token') || 
                      hash.includes('type=recovery') ||
                      hash.includes('#access_token');
    
    if (isRecovery && !passwordResetInitiated.current) {
      console.log('🔐 Recovery link detected IMMEDIATELY - showing password reset form');
      passwordResetInitiated.current = true;
      setIsPasswordReset(true);
      setStatus('success');
      setMessage('Please set a new password');
    }
  }, []); // Run only once on mount

  useEffect(() => {
    // Prevent multiple checks
    if (hasChecked.current) return;
    
    const handleEmailLink = async () => {
      hasChecked.current = true;
      
      try {
        // Check URL hash IMMEDIATELY before Supabase processes it
        const hash = window.location.hash;
        const token = searchParams.get('token');
        const type = searchParams.get('type');
        
        // Check for error parameters (when Supabase redirects with errors)
        const error = searchParams.get('error');
        const errorCode = searchParams.get('error_code');
        const errorDescription = searchParams.get('error_description');
        
        console.log('Email confirmation params:', { token, type, hash, error, errorCode, errorDescription, fullUrl: window.location.href });
        
        // If there's an error but we have a token, try to verify it directly
        if (error && token && (errorCode === 'otp_expired' || error.includes('expired'))) {
          console.warn('⚠️ Token appears expired, but attempting to verify anyway...');
          // Don't return - continue to see if we can still verify
        }
        
        // Check if this is a password reset (recovery) link
        // Supabase password reset links include #access_token=... in the hash
        const isRecovery = type === 'recovery' || 
                          hash.includes('access_token') || 
                          hash.includes('type=recovery') ||
                          hash.includes('#access_token') ||
                          (token && !type); // If we have a token but no type, might be recovery
        
        if (isRecovery) {
          console.log('🔐 Recovery link confirmed - ensuring password reset form is shown');
          
          // Ensure password reset form is showing
          if (!passwordResetInitiated.current) {
            setIsPasswordReset(true);
            setStatus('success');
            setMessage('Please set a new password');
            passwordResetInitiated.current = true;
          }
          
          // Explicitly trigger Supabase to process the hash and create session
          // This ensures the session exists when the user clicks "Update Password"
          if (hash && hash.includes('access_token')) {
            console.log('🔑 Processing recovery hash to create session...');
            
            // Try to extract access token from hash manually as fallback
            const hashParams = new URLSearchParams(hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token');
            
            if (accessToken && refreshToken) {
              console.log('🔑 Found tokens in hash, setting session explicitly...');
              // Set session explicitly using the tokens from the hash
              const { data: { session: explicitSession }, error: setError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
              });
              
              if (explicitSession && !setError) {
                console.log('✅ Session set explicitly from hash tokens:', explicitSession.user.email);
                sessionStored.current = true;
              } else if (setError) {
                console.warn('⚠️ Could not set session from tokens:', setError.message);
              }
            }
            
            // Also get session normally (Supabase should process hash automatically)
            const { data: { session: initialSession }, error: sessionErr } = await supabase.auth.getSession();
            if (initialSession) {
              console.log('✅ Session created immediately:', initialSession.user.email);
              sessionStored.current = true;
            } else if (sessionErr) {
              console.warn('⚠️ Session not yet created, will wait for auth state change:', sessionErr.message);
            }
          }
          
          // Listen for auth state change - Supabase will auto-authenticate
          // Look for PASSWORD_RECOVERY or SIGNED_IN events
          let subscription;
          const { data: { subscription: sub } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('Auth state change on recovery page:', event, session?.user?.email);
            
            if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && session?.user) {
              console.log('✅ User authenticated via recovery link:', session.user.email, 'Event:', event);
              // Password form is already showing, just ensure state is correct
              setIsPasswordReset(true);
              setStatus('success');
              setMessage('Please set a new password');
              sessionStored.current = true;
              
              if (subscription) {
                subscription.unsubscribe();
              }
            }
          });
          subscription = sub;
          
          // Also check if Supabase already processed it (retry after a short delay)
          await new Promise(resolve => setTimeout(resolve, 500));
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.user) {
            console.log('✅ Session confirmed (Supabase auto-authenticated):', session.user.email);
            // Form is already showing, no need to do anything
            setIsPasswordReset(true);
            setStatus('success');
            setMessage('Please set a new password');
            sessionStored.current = true;
          } else {
            console.warn('⚠️ No session yet, but form is showing. User may need to click link again if session expires.');
          }
          
          // Cleanup subscription after 5 seconds
          setTimeout(() => {
            if (subscription) {
              subscription.unsubscribe();
            }
          }, 5000);
        } else if ((type === 'signup' || type === 'recovery' || !type) && token) {
          // Handle email confirmation for signup or recovery
          // Try recovery first if type is recovery or unknown
          const verifyType = type === 'recovery' ? 'recovery' : (type === 'signup' ? 'email' : 'recovery');
          
          console.log(`🔐 Attempting to verify OTP token with type: ${verifyType}`);
          
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: verifyType
          });
          
          console.log('OTP verification result:', { data, error });
          
          if (error && verifyType === 'recovery') {
            // If recovery failed, try email type as fallback
            console.log('⚠️ Recovery verification failed, trying email type...');
            const { data: emailData, error: emailError } = await supabase.auth.verifyOtp({
              token_hash: token,
              type: 'email'
            });
            
            if (!emailError && emailData) {
              console.log('✅ Verified with email type instead');
              setStatus('success');
              setMessage('Email confirmed successfully! Welcome to Swirl!');
              setTimeout(() => navigate('/'), 3000);
              return;
            }
          }
          
          if (!error && data) {
            // If verification succeeded, check if it's a recovery
            if (verifyType === 'recovery' || data.user) {
              console.log('✅ OTP verified, checking if this is password recovery...');
              // Check current session - if user is logged in via recovery, show password form
              const { data: { session } } = await supabase.auth.getSession();
              if (session?.user) {
                console.log('✅ User session created from OTP verification:', session.user.email);
                setIsPasswordReset(true);
                setStatus('success');
                setMessage('Please set a new password');
                return;
              }
            }
            
            setStatus('success');
            setMessage('Email confirmed successfully! Welcome to Swirl!');
            setTimeout(() => navigate('/'), 3000);
          } else {
            console.error('OTP verification error:', error);
            setStatus('error');
            setMessage(error?.message || 'Invalid or expired confirmation link. Please request a new one.');
          }
        } else {
          setStatus('error');
          setMessage('Invalid confirmation link. Please check your email and try again.');
        }
      } catch (error) {
        console.error('Email confirmation error:', error);
        setStatus('error');
        setMessage('An error occurred during confirmation. Please try again.');
      }
    };

    handleEmailLink();
  }, [searchParams, navigate]);

  const handlePasswordReset = async (e) => {
    e?.preventDefault();
    
    // Validate passwords
    if (!newPassword || newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setPasswordError('');
    setResetting(true);
    
    try {
      // Check if we have an active session
      let { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      console.log('Current session before password update:', session?.user?.email || 'none');
      
      // If no session, try to restore from URL hash
      if (!session) {
        console.log('⚠️ No session found, attempting to restore from URL hash...');
        const hash = window.location.hash;
        
        // Try to extract and set the session from hash
        if (hash.includes('access_token')) {
          // Extract tokens from hash
          const hashParams = new URLSearchParams(hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          
          if (accessToken && refreshToken) {
            console.log('🔑 Attempting to restore session from hash tokens...');
            // Set session explicitly using the tokens from the hash
            const { data: { session: restoredSession }, error: restoreError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (restoredSession && !restoreError) {
              console.log('✅ Session restored from hash:', restoredSession.user.email);
              session = restoredSession;
            } else if (restoreError) {
              console.error('❌ Failed to restore session from hash:', restoreError.message);
              // Try one more time with getSession
              const { data: { session: refreshedSession } } = await supabase.auth.getSession();
              session = refreshedSession;
            }
          } else {
            // Fallback: try to get session normally
            const { data: { session: refreshedSession } } = await supabase.auth.getSession();
            session = refreshedSession;
          }
        }
      }
      
      // If still no session, show error
      if (!session) {
        console.error('❌ No active session - cannot update password');
        setPasswordError('Session expired. Please request a new password reset link.');
        setResetting(false);
        return;
      }
      
      console.log('✅ Session found, updating password for:', session.user.email);
      
      // Now update the password
      const { error } = await updatePassword(newPassword);
      
      if (error) {
        console.error('Password update error:', error);
        setPasswordError(error.message || 'Failed to update password. Please try again.');
      } else {
        setStatus('success');
        setMessage('Password updated successfully!');
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      console.error('Password reset exception:', error);
      setPasswordError('An unexpected error occurred. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {status === 'loading' && (
          <div className="text-center">
            <Loader className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Confirming Email</h1>
            <p className="text-gray-600">{message}</p>
          </div>
        )}
        
        {isPasswordReset && status === 'success' && (
          <div>
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Your Password</h1>
              <p className="text-gray-600">{message}</p>
            </div>
            
            <form onSubmit={handlePasswordReset} className="space-y-4">
              {/* New Password */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new password"
                    required
                    minLength={6}
                    disabled={resetting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    required
                    minLength={6}
                    disabled={resetting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Error Message */}
              {passwordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{passwordError}</p>
                </div>
              )}
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={resetting || !newPassword || !confirmPassword}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {resetting ? 'Updating Password...' : 'Update Password'}
              </button>
            </form>
          </div>
        )}
        
        {!isPasswordReset && status === 'success' && (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Confirmed!</h1>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500 mt-4">Redirecting to your dashboard...</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Confirmation Failed</h1>
            <p className="text-gray-600">{message}</p>
            <div className="mt-6 space-y-3">
              <button 
                onClick={() => navigate('/auth')}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button 
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmation;
